<script setup lang="ts">
//
// Imports
//

// Components
import Header from "~/components/landing/Header.vue";
import MainSection from "~/components/landing/MainSection.vue";

// Layout
definePageMeta({
  layout: "default",
});

//
// Initial Setup
//

// Handle navigation
const router = useRouter();

// Localization
const { locale } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const selectedLanguage = ref<string>(locale.value);

// API Client
let cl = await useProdClient();
const ac = new AbortController();

//
// Reactive Data
//

// Laboratory Logo
const loadingLabLogo = ref<boolean>(true);
const format = ref<"svg" | "png">("svg");
const logoUrl = ref<string>("");

// Laboratory Name
const loadingLabName = ref<boolean>(true);
const laboratoryName = ref<string>("");

// Laboratory Terms Of Use
const loadingLabTermsOfUse = ref<boolean>(true);
const laboratoryTermsOfUse = ref<string>("");

//
// Methods
//

/**
 * Fetches the laboratory logo for the given format and updates the state.
 * @param format - The format of the logo to fetch (e.g., "svg", "png").
 */
const fetchLabLogo = async (format: "svg" | "png"): Promise<void> => {
  try {
    loadingLabLogo.value = true;

    // Fetch and update the laboratory logo
    logoUrl.value = await cl.fetchLaboratoryLogo(format, ac.signal);
  } catch (err) {
    console.error("Error fetching laboratory logo:", err);
  } finally {
    loadingLabLogo.value = false;
  }
};

/**
 * Fetches the laboratory name for the given locale and updates the state.
 * @param locale - The locale code to fetch the laboratory name for (e.g., "en", "bg").
 */
const fetchLabName = async (locale: string) => {
  try {
    loadingLabName.value = true;

    // Fetch and update the laboratory name
    laboratoryName.value = await cl.fetchLaboratoryName(locale, ac.signal);
  } catch (err) {
    console.error("Error fetching laboratory name:", err);
  } finally {
    loadingLabName.value = false;
  }
};

/**
 * Fetches the lab terms of use data from the API.
 * @returns {Promise<void>} - Resolves once the data is fetched and state is updated.
 */
const fetchLabTermsOfUse = async (): Promise<void> => {
  try {
    loadingLabTermsOfUse.value = true;

    // Fetch and update the laboratory terms of use
    laboratoryTermsOfUse.value = await cl.fetchLaboratoryTermsOfUse(ac.signal);
  } catch (err) {
    console.error("Error fetching laboratory terms of use:", err);
  } finally {
    loadingLabTermsOfUse.value = false;
  }
};

//
// On Component Mount
//

// Locale
onMounted(() => {
  const cookieLocale = useCookie("i18n_redirected").value;

  if (cookieLocale === "en" || cookieLocale === "bg") {
    // Set selected language based on cookie
    selectedLanguage.value = cookieLocale;
    locale.value = cookieLocale;

    const newPath = switchLocalePath(selectedLanguage.value as "en" | "bg");
    if (newPath) {
      router.push(newPath);
    }
  }
});

// Laboratory logo
onMounted(() => fetchLabLogo(format.value));

// Laboratory name
onMounted(() => fetchLabName(locale.value));

// Laboratory terms of use
onMounted(() => fetchLabTermsOfUse());

//
// Watches
//

// Watch for locale changes and fetch the laboratory name again
watch(
  () => locale.value,
  (newLocale) => fetchLabName(newLocale)
);

//
// Metadata
//

// Set the page's head metadata
useHead({
  link: [{ rel: "icon", type: "image/svg+xml", href: logoUrl }],
  title: laboratoryName,
  htmlAttrs: {
    lang: locale,
  },
  meta: [
    {
      name: "description",
      content:
        "A comprehensive platform for managing medical laboratory operations, patient data, and test results.",
    },
    {
      name: "keywords",
      content: "medical laboratory, patient data, test results, healthcare",
    },
    { name: "author", content: "SKYWARE Group | LIMS iLab" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
  ],
});
</script>

<template>
  <v-container max-width="1024" class="mx-auto">
    <!-- Logo and Company Info -->
    <Header
      v-if="!loadingLabName"
      :logoUrl="logoUrl"
      :laboratoryName="laboratoryName"
      :isLaboratoryNameLoading="loadingLabName"
    />

    <!-- Main Section -->
    <MainSection
      :loadingLabTermsOfUse="loadingLabTermsOfUse"
      :laboratoryTermsOfUse="laboratoryTermsOfUse"
    />
  </v-container>
</template>
